import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../../redux/alertsSlice";
import { Table, Row, Col, Input, Modal, Button, Form, Select, } from "antd";
import toast from "react-hot-toast";


const { Option } = Select;

function ManageReviews() {
  const dispatch = useDispatch();
  const [reviews, setReviews] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [form] = Form.useForm();

  const fetchReviews = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/admin/get-all-reviews", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) {
        setReviews(res.data.data);
      } else {
        toast.error("Failed to fetch reviews");
      }
    } catch (error) {
      dispatch(hideLoading());
      console.error(error);
      toast.error("Something went wrong");
    }
  }, [dispatch]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const openEditModal = (record) => {
    setSelectedReview(record);
    form.setFieldsValue({
      status: record.status,
    });
    setIsModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
     dispatch(showLoading());
      const values = await form.validateFields();
      const response = await axios.post(
        "/api/admin/update-review-status",
        { reviewId: selectedReview._id, status: values.status, adminComment: values.adminComment },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      dispatch(hideLoading());
      if (response.data.success) {
        toast.success("Review status updated successfully");
        setIsModalVisible(false);
        fetchReviews();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error updating review status");
    }
  };



  const clearFilters = () => setFilteredInfo({});
  const clearAll = () => {
    setFilteredInfo({});
    setSortedInfo({});
  };




 const columns = [
  {
    title: "Patient Name",
    dataIndex: "patientName",
    key: "patientName",
    filteredValue: filteredInfo.patientName || null,
    sorter: (a, b) => a.patientName.localeCompare(b.patientName),
    sortOrder: sortedInfo.columnKey === "patientName" ? sortedInfo.order : null,
    onFilter: (value, record) =>
      record.patientName.toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Doctor Name",
    dataIndex: "doctorName",
    key: "doctorName",
    sorter: (a, b) => a.doctorName.localeCompare(b.doctorName),
    filteredValue: filteredInfo.doctorName || null,
    onFilter: (value, record) =>
      record.doctorName.toLowerCase().includes(value.toLowerCase()),
  },

  
    {
      title: "Rating",
      dataIndex: "rating",
      key: "rating",
      sorter: (a, b) => a.rating - b.rating,
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      render: (text) => <span>{text?.slice(0, 50)}...</span>,
    },
    {
    title: "Status",
    dataIndex: "status",
    key: "status",
    filters: [
      { text: "Approved", value: "approved" },
      { text: "Hold",  value: "hold"  },
      { text: "Rejected", value: "rejected" },
    ],
    filteredValue: filteredInfo.status || null,
    onFilter: (value, record) => (record.status || "").toLowerCase() === value,
    render: (text) => {
      const status = (text || "unknown").toLowerCase();          // ← safe fallback
      const color =
        status === "approved"
          ? "green"
          : status === "hold"
          ? "orange"
          : status === "rejected"
          ? "red"
          : "grey";
      return (
        <span style={{ color }}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    },
      },

    {
  title: "Actions",
  key: "actions",
  render: (_, record) => (
    <Button
      type="primary"
      onClick={() => openEditModal(record)}
      disabled={record.status === "approved" || record.status === "rejected"}
    >
      Update Feedback
    </Button>
  ),
},

  ];

  const formattedReviews = reviews.map((r) => ({
  ...r,
  key: r._id,
  patientName: r.userId?.name || "",
  doctorName: `${r.doctorId?.firstName || ""} ${r.doctorId?.lastName || ""}`.trim(),
  status: r.status || "unknown",          // ensure a string
}));



  return (
    <Layout>
      <Row gutter={20} style={{ marginBottom: 16 }}>
        <Col span={8} xs={24} sm={24} lg={6}>
          <Input
            placeholder="Search by patient name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </Col>
        <Col>
          <Button onClick={clearFilters} style={{ marginRight: 8 }}>
            Clear filters
          </Button>
          <Button onClick={clearAll}>Clear filters and sorters</Button>
        </Col>
      </Row>

 <Table
  columns={columns}
  dataSource={formattedReviews.filter((r) =>
    r.patientName.toLowerCase().includes(searchTerm.toLowerCase())
  )}
  /* …props… */
/>


     <Modal
  title="Update Review Status"
  open={isModalVisible}
  onCancel={() => setIsModalVisible(false)}
  footer={[
     <Button key="submit" type="primary"  className="btn-primary" onClick={handleEditSubmit}>
      Update
    </Button>,
    <Button key="cancel"  className="btn-2" onClick={() => setIsModalVisible(false)}>
      Cancel
    </Button>,
   
  ]}
  destroyOnClose
>
  <Form form={form} layout="vertical" preserve={false}>
    <Form.Item
      label="Review Status"
      name="status"
      rules={[{ required: true, message: "Please select a status" }]}
    >
      <Select placeholder="Select status">
        <Option value="approved">Approved</Option>
        <Option value="hold">Hold</Option>
        <Option value="rejected">Rejected</Option>
      </Select>
    </Form.Item>

    <Form.Item
      label="Admin Comment"
      name="adminComment"
      rules={[{ required: true, message: "Please enter a comment" }]}
    >
      <Input.TextArea rows={4} placeholder="Enter comment for this review decision" />
    </Form.Item>
  </Form>
</Modal>

    </Layout>
  );
}

export default ManageReviews;
